import { roleAtLeast, type Role } from '@bartizo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { createAction } from '../api/actions.js';
import { getConfigLayer, getConfigVersion, putConfigLayer } from '../api/config.js';
import { getCrowdsecHealth } from '../api/servers.js';
import { useCurrentUser } from '../auth/session-context.js';
import {
  collectionsView,
  withCrowdsec,
  type CollectionRow,
  type CollectionSource,
} from '../lib/crowdsec-collections.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { DataTable, type Column } from '../ui/DataTable.js';
import { ConfirmDialog } from './ConfirmDialog.js';

/**
 * CrowdSec's collections on one server (P11.9.2; knowledge doc sections 8.1, 11).
 *
 * What is installed and who put it there — the installer, the settings, or
 * something else (a person, or a collection that brought it) — which ones only
 * watch, and what would suit the server's roles. An admin adds or removes the
 * settings' collections and sets one to watch or enforce, in this server's own
 * layer, through the same settings route every other change takes (rule 9's
 * audit, a new version for the agent). Removing reaches only what Bartizo
 * installed: the helper keeps that record, not this page.
 *
 * Upgrading the hub's items is an action, not a setting: an operator asks, the
 * server does it once, and the result is on the action's page.
 */

const SOURCE_LABEL: Record<CollectionSource, string> = {
  settings: 'Settings',
  installer: 'Installer',
  other: 'By hand, or brought by another',
};

export function CrowdsecCollectionsCard({
  serverId,
  roles,
  installed,
  latestVersion,
}: {
  serverId: string;
  roles: readonly Role[];
  installed: boolean | undefined;
  latestVersion: number | null;
}): ReactNode {
  const session = useCurrentUser();
  const isAdmin = session.user.role === 'admin';
  const canAct = roleAtLeast(session.user.role, 'operator');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [confirmUpgrade, setConfirmUpgrade] = useState(false);
  const scopeRef = { scope: 'server', id: serverId } as const;

  const health = useQuery({
    queryKey: ['crowdsec-health', serverId],
    queryFn: () => getCrowdsecHealth(serverId),
    enabled: installed === true,
  });
  // The settings are an admin's to read and change (section 17).
  const current = useQuery({
    queryKey: ['config-version', serverId, latestVersion],
    queryFn: () => getConfigVersion(serverId, latestVersion ?? 0),
    enabled: installed === true && isAdmin && latestVersion !== null,
  });
  const layer = useQuery({
    queryKey: ['config-layer', 'server', serverId],
    queryFn: () => getConfigLayer(scopeRef),
    enabled: installed === true && isAdmin,
  });

  const change = useMutation({
    mutationFn: async (next: Parameters<typeof withCrowdsec>[1]) => {
      // Read again just before writing, so a change somebody made meanwhile stays.
      const fresh = await getConfigLayer(scopeRef);
      return putConfigLayer(scopeRef, withCrowdsec(fresh.layer?.settings ?? {}, next));
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['config-layer'] });
      void queryClient.invalidateQueries({ queryKey: ['config-versions', serverId] });
      void queryClient.invalidateQueries({ queryKey: ['server', serverId] });
    },
  });

  const upgrade = useMutation({
    mutationFn: () =>
      createAction({
        type: 'upgrade_crowdsec_hub',
        target: { kind: 'server', id: serverId },
        args: {},
      }),
    onSuccess: (action) => {
      setConfirmUpgrade(false);
      void navigate(`/actions/${encodeURIComponent(action.id)}`);
    },
  });

  if (installed !== true) return null;

  const report = health.data?.report ?? null;
  const merged = current.data?.document.modules.crowdsec;
  const view = collectionsView({
    hub: report?.hub ?? null,
    roles,
    merged,
    own: layer.data?.layer?.settings.modules?.crowdsec,
  });
  const hub = report?.hub ?? null;
  const busy = change.isPending;

  const columns: Column<CollectionRow>[] = [
    {
      key: 'name',
      header: 'Collection',
      rowHeader: true,
      sortValue: (row) => row.name,
      cell: (row) => (
        <>
          {row.name}
          {row.version !== null && <span className="muted"> {row.version}</span>}
          {row.advice !== undefined && <span className="cell-sub">{row.advice.title}</span>}
        </>
      ),
    },
    {
      key: 'source',
      header: 'Put there by',
      sortValue: (row) => row.source,
      cell: (row) => SOURCE_LABEL[row.source],
    },
    {
      key: 'state',
      header: 'State',
      cell: (row) => (
        <span className="badge-list">
          {!row.installed && (
            <span
              className="badge badge-accent"
              title="In the settings, but not in CrowdSec's hub when the agent last looked. The agent installs it through the root helper and looks again straight after; if it stays, the crowdsec module above says why."
            >
              Waiting to install
            </span>
          )}
          {row.mode === 'watch' ? (
            <span className="badge badge-warning">Watching</span>
          ) : (
            row.installed && <span className="badge badge-success">Enforcing</span>
          )}
          {row.tainted && <span className="badge badge-warning">Edited here</span>}
          {row.outdated && <span className="badge badge-muted">Newer version</span>}
        </span>
      ),
    },
    ...(isAdmin
      ? [
          {
            key: 'actions',
            header: 'Change',
            cell: (row: CollectionRow) => (
              <span className="row">
                <Button
                  disabled={busy}
                  onClick={() => {
                    change.mutate({
                      kind: 'mode',
                      name: row.name,
                      mode: row.mode === 'watch' ? 'enforce' : 'watch',
                    });
                  }}
                >
                  {row.mode === 'watch' ? 'Enforce' : 'Watch only'}
                </Button>
                {row.ownLayer && (
                  <Button
                    variant="danger"
                    disabled={busy}
                    onClick={() => {
                      change.mutate({ kind: 'remove', name: row.name });
                    }}
                  >
                    Remove
                  </Button>
                )}
              </span>
            ),
          },
        ]
      : []),
  ];

  return (
    <Card
      title="CrowdSec collections"
      description="What CrowdSec detects on this server, who added it, and what only watches. Watching scenarios raise events and ban nobody."
      aside={
        canAct &&
        hub !== null &&
        hub.outdated > 0 && (
          <Button
            onClick={() => {
              setConfirmUpgrade(true);
            }}
          >
            Upgrade {hub.outdated} hub {hub.outdated === 1 ? 'item' : 'items'}
          </Button>
        )
      }
      footer={
        isAdmin ? (
          <span className="text-sm muted">
            Changes go into this server’s own settings. For a group or every server,{' '}
            <Link to="/settings">edit those settings</Link>.
          </span>
        ) : undefined
      }
    >
      {health.isError && (
        <Alert tone="danger" title="Could not load CrowdSec’s collections">
          Try again in a moment.
        </Alert>
      )}
      {change.isError && (
        <Alert tone="danger" title="That change was not saved">
          {change.error instanceof Error ? change.error.message : 'Try again in a moment.'}
        </Alert>
      )}
      {hub?.globalSimulation === true && (
        <Alert tone="warning" title="Everything on this server only watches">
          <code>simulation: true</code> is set in <code>/etc/crowdsec/simulation.yaml</code>, so
          CrowdSec bans nobody here. Bartizo leaves that choice alone.
        </Alert>
      )}
      {health.data !== undefined && hub === null && (
        <p className="muted text-sm">
          Not reported yet. The agent lists CrowdSec’s collections once it and its root helper are
          up to date.
        </p>
      )}
      {hub !== null && (
        <DataTable
          caption="CrowdSec collections on this server"
          columns={columns}
          rows={view.rows}
          rowKey={(row) => row.name}
          empty={<p className="muted text-sm">No collection is installed.</p>}
        />
      )}
      {hub !== null && hub.simulated.length > 0 && (
        <p className="text-sm muted">
          {hub.simulated.length}{' '}
          {hub.simulated.length === 1 ? 'scenario watches' : 'scenarios watch'} on this server now
          {hub.managed.simulation.length < hub.simulated.length
            ? `, ${String(hub.simulated.length - hub.managed.simulation.length)} of them set by hand`
            : ''}
          .
        </p>
      )}

      {hub !== null && view.suggestions.length > 0 && (
        <section>
          <h3>Worth adding here</h3>
          <ul className="stack">
            {view.suggestions.map((entry) => (
              <li key={entry.name}>
                <strong>{entry.title}</strong> <code>{entry.name}</code>
                <p className="text-sm">{entry.why}</p>
                {entry.when !== undefined && <p className="text-sm muted">Only if: {entry.when}</p>}
                {entry.needs !== undefined && (
                  <p className="text-sm muted">Needs first: {entry.needs}</p>
                )}
                {isAdmin && (
                  <span className="row">
                    <Button
                      disabled={busy}
                      onClick={() => {
                        change.mutate({ kind: 'add', name: entry.name, watch: true });
                      }}
                    >
                      Add, watching first
                    </Button>
                    <Button
                      variant="secondary"
                      disabled={busy}
                      onClick={() => {
                        change.mutate({ kind: 'add', name: entry.name, watch: false });
                      }}
                    >
                      Add and enforce
                    </Button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {confirmUpgrade && hub !== null && (
        <ConfirmDialog
          title="Upgrade CrowdSec’s hub items?"
          description={`${String(hub.outdated)} installed ${hub.outdated === 1 ? 'item has' : 'items have'} a newer version in the CrowdSec Hub.`}
          confirmLabel="Upgrade"
          busy={upgrade.isPending}
          error={
            upgrade.isError ? 'The upgrade could not be asked for. Try again in a moment.' : null
          }
          onConfirm={() => {
            upgrade.mutate();
          }}
          onCancel={() => {
            setConfirmUpgrade(false);
          }}
        >
          <p className="text-sm">
            The server fetches the hub’s index again, upgrades what is outdated and reloads
            CrowdSec. Items edited on the server are left as they are.
          </p>
        </ConfirmDialog>
      )}
    </Card>
  );
}
